import { Base } from '../../abstract/base.model';
import { CertificationOwnerType } from './certification-owner-type.model';
import { CertificationStatus } from './certification-status.model';
import { CertificationType } from './certification-type.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * A certification that belongs to an organization or a user profile (hub `data-models/certification.ts`).
 * Dates stay as the hub's ISO strings.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays `null`).
 */
export class Certification extends Base {
  // Set on create only
  certificationType?: CertificationType | null;
  ownerType?: CertificationOwnerType | null;
  /** Set when ownerType is 'organization'. */
  organizationId?: string | null;
  /** Set when ownerType is 'profile'. */
  profileId?: string | null;
  previousCertificationId?: string | null;

  // Writable fields
  certificateNumber?: string | null;
  issuedDate?: string | null;
  expirationDate?: string | null;
  issuingAuthority?: string | null;
  /** For state licenses. */
  state?: string | null;
  documentUrl?: string | null;
  documentFileName?: string | null;
  notes?: string | null;
  status?: CertificationStatus | null;

  // Read-only
  createdBy?: string | null;
  updatedBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.certificationType = nullableString(source['certificationType']) as CertificationType | null | undefined;
    this.ownerType = nullableString(source['ownerType']) as CertificationOwnerType | null | undefined;
    this.organizationId = nullableString(source['organizationId']);
    this.profileId = nullableString(source['profileId']);
    this.previousCertificationId = nullableString(source['previousCertificationId']);
    this.certificateNumber = nullableString(source['certificateNumber']);
    this.issuedDate = nullableString(source['issuedDate']);
    this.expirationDate = nullableString(source['expirationDate']);
    this.issuingAuthority = nullableString(source['issuingAuthority']);
    this.state = nullableString(source['state']);
    this.documentUrl = nullableString(source['documentUrl']);
    this.documentFileName = nullableString(source['documentFileName']);
    this.notes = nullableString(source['notes']);
    this.status = nullableString(source['status']) as CertificationStatus | null | undefined;
    this.createdBy = nullableString(source['createdBy']);
    this.updatedBy = nullableString(source['updatedBy']);
  }
}
